import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  Fade,
  Paper,
} from "@mui/material";
import { styled } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { useTheme } from "@mui/material/styles";

// Safe gradient container with hardcoded colors
const GradientContainer = styled(Paper)({
  background: `linear-gradient(135deg, #f5f5f5, #e0e0e0, #f5f5f5)`,
  borderRadius: 16,
  padding: 24,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: `radial-gradient(circle, rgba(25, 118, 210, 0.05) 10%, transparent 40%)`,
    animation: "pulse 15s infinite linear",
  },
  "@keyframes pulse": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
});

// Enhanced styled card with hardcoded colors
const MCQCard = styled(Card)(
  ({ selected, correct, incorrect, waitingToProceed }) => ({
    cursor: !waitingToProceed ? "pointer" : "default",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    height: "100%",
    position: "relative",
    boxShadow: selected
      ? `0 8px 16px -2px rgba(25, 118, 210, 0.3)`
      : "0 2px 8px rgba(0, 0, 0, 0.08)",
    border: selected
      ? `2px solid #1976d2`
      : "2px solid transparent",
    borderRadius: 12,
    backgroundColor: incorrect
      ? "#ffebee"
      : correct
      ? "#e8f5e9"
      : selected
      ? "#e3f2fd"
      : "#ffffff",
    "&:hover": {
      boxShadow: !waitingToProceed
        ? `0 12px 20px -4px rgba(25, 118, 210, 0.2)`
        : "0 2px 8px rgba(0, 0, 0, 0.08)",
      transform: !waitingToProceed && !selected ? "translateY(-4px)" : "none",
      backgroundColor: !waitingToProceed && !correct && !incorrect
        ? selected
          ? "#bbdefb"
          : "#f5f5f5"
        : undefined,
    },
    "&::after": correct || incorrect
      ? {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          background: correct
            ? "radial-gradient(circle at center, rgba(165, 214, 167, 0.6), transparent 70%)"
            : "radial-gradient(circle at center, rgba(239, 154, 154, 0.6), transparent 70%)",
        }
      : {},
  })
);

// Animated hotkey indicator
const HotkeyIndicator = styled(Box)({
  position: "absolute",
  top: 8,
  right: 8,
  background: "#e0e0e0",
  color: "#757575",
  fontSize: "0.75rem",
  width: 24,
  height: 24,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.1)",
    background: "#1976d2",
    color: "#fff",
  },
});

// Enhanced success icon with animation
const SuccessIcon = styled(CheckCircleIcon)({
  backgroundColor: "#4caf50",
  color: "#fff",
  borderRadius: "50%",
  padding: 3,
  position: "absolute",
  bottom: 8,
  right: 8,
  fontSize: "2rem",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  animation: "popIn 0.4s cubic-bezier(0.17, 0.67, 0.26, 1.2) forwards",
  "@keyframes popIn": {
    "0%": { transform: "scale(0)" },
    "80%": { transform: "scale(1.2)" },
    "100%": { transform: "scale(1)" },
  },
});

// Enhanced error icon with animation
const ErrorIcon = styled(CancelIcon)({
  backgroundColor: "#f44336",
  color: "#fff",
  borderRadius: "50%",
  padding: 3,
  position: "absolute",
  bottom: 8,
  right: 8,
  fontSize: "2rem",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  animation: "shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both",
  "@keyframes shake": {
    "0%, 100%": { transform: "translateX(0)" },
    "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-4px)" },
    "20%, 40%, 60%, 80%": { transform: "translateX(4px)" },
  },
});

// Nice pulsing button with hardcoded colors
const ActionButton = styled(Button)(({ waiting }) => ({
  position: "relative",
  padding: "10px 32px",
  fontSize: "1rem",
  fontWeight: "bold",
  borderRadius: 16,
  transition: "all 0.3s ease",
  background: waiting
    ? `linear-gradient(45deg, #1976d2, #1565c0)`
    : `linear-gradient(45deg, #1976d2, #1565c0)`,
  boxShadow: waiting
    ? `0 6px 15px -4px rgba(25, 118, 210, 0.5)`
    : `0 4px 12px -2px rgba(25, 118, 210, 0.3)`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: waiting
      ? `0 8px 20px -4px rgba(25, 118, 210, 0.6)`
      : `0 6px 16px -2px rgba(25, 118, 210, 0.4)`,
  },
  "&:disabled": {
    background: "#bdbdbd",
    boxShadow: "none",
  },
  "&::after": waiting
    ? {
        content: '""',
        position: "absolute",
        inset: "-4px",
        borderRadius: "inherit",
        padding: "4px",
        background: `linear-gradient(45deg, #1976d2, #9e9e9e)`,
        WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
        animation: "pulseBorder 1.5s ease infinite",
      }
    : {},
  "@keyframes pulseBorder": {
    "0%": { opacity: 0.6 },
    "50%": { opacity: 1 },
    "100%": { opacity: 0.6 },
  },
}));

// Instructions component
const Instructions = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 16,
  padding: 12,
  borderRadius: 4,
  backgroundColor: "#e3f2fd",
  border: `1px solid #90caf9`,
});

const MCQAnswerInput = ({
  options,
  onSubmitAnswer,
  waitingToProceed,
  onProceed,
  correctAnswer,
}) => {
  const [selected, setSelected] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [animateButton, setAnimateButton] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  
  // Log theme structure for debugging
  useEffect(() => {
    console.log("Theme object:", theme);
  }, [theme]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Handle Enter key
      if (e.key === "Enter") {
        e.preventDefault(); // prevent form submission or default behaviors
        if (waitingToProceed) {
          onProceed();
        } else if (!submitted && selected) {
          handleSubmit();
        }
        return;
      }

      // Don't process number keys if already submitted
      if (submitted) return;

      // Check if the key is a number between 1 and the number of options
      const num = parseInt(e.key);
      if (!isNaN(num) && num >= 1 && num <= options.length) {
        e.preventDefault();
        setSelected(options[num - 1]);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [options, waitingToProceed, submitted, onProceed, selected]);

  // Reset the component when options change
  useEffect(() => {
    setSelected("");
    setSubmitted(false);
  }, [options]);

  // Add button animation
  useEffect(() => {
    if (waitingToProceed) {
      const interval = setInterval(() => {
        setAnimateButton(prev => !prev);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [waitingToProceed]);

  const handleCardSelect = (option) => {
    if (!waitingToProceed && !submitted) {
      setSelected(option);
    }
  };

  const handleSubmit = () => {
    if (selected) {
      onSubmitAnswer(selected);
      setSubmitted(true);
    }
  };

  return (
    <GradientContainer elevation={3}>
      {!submitted && !waitingToProceed && (
        <Instructions>
          <LightbulbIcon style={{ color: "#2196f3" }} fontSize="small" />
          <Typography variant="body2">
            Select an answer and click Submit, or use number keys [1-{options.length}] to select and Enter to submit.
          </Typography>
        </Instructions>
      )}
      
      {submitted && !waitingToProceed && (
        <Instructions sx={{ 
          backgroundColor: "#e8f5e9", 
          borderColor: "#a5d6a7"
        }}>
          <CheckCircleIcon style={{ color: "#4caf50" }} fontSize="small" />
          <Typography variant="body2">
            Answer submitted. {selected === correctAnswer ? "Great job!" : "Review the correct answer highlighted below."}
          </Typography>
        </Instructions>
      )}
      
      {waitingToProceed && (
        <Instructions sx={{ 
          backgroundColor: "#e3f2fd", 
          borderColor: "#90caf9"
        }}>
          <Typography variant="body2">
            Press "Proceed" or hit Enter to continue to the next question.
          </Typography>
        </Instructions>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {options.map((option, idx) => {
          const isCorrect = submitted && option === correctAnswer;
          const isIncorrect = submitted && selected === option && option !== correctAnswer;

          return (
            <Grid item xs={12} sm={6} key={idx}>
              <Fade 
                in={true} 
                timeout={400} 
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <MCQCard
                  selected={selected === option}
                  correct={isCorrect}
                  incorrect={isIncorrect}
                  waitingToProceed={waitingToProceed}
                  onClick={() => handleCardSelect(option)}
                  elevation={selected === option ? 4 : 1}
                >
                  <CardContent sx={{ p: 3 }}>
                    <HotkeyIndicator>
                      {idx + 1}
                    </HotkeyIndicator>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        wordBreak: "break-word",
                        py: 1,
                        fontWeight: selected === option ? 500 : 400,
                      }}
                    >
                      {option}
                    </Typography>
                    {isCorrect && <SuccessIcon />}
                    {isIncorrect && <ErrorIcon />}
                  </CardContent>
                </MCQCard>
              </Fade>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ActionButton
          variant="contained"
          onClick={waitingToProceed ? onProceed : handleSubmit}
          disabled={!waitingToProceed && (!selected || submitted)}
          size="large"
          waiting={waitingToProceed ? 1 : 0}
          className={animateButton && waitingToProceed ? "pulse" : ""}
        >
          {waitingToProceed ? "Proceed" : "Submit"}
        </ActionButton>
      </Box>
    </GradientContainer>
  );
};

export default MCQAnswerInput;